/**
 * PARAFON ÖNCESİ KAYIT DOSYASI — kullanıcının eski veritabanı uygulamasının
 * dışa aktarımı (09.10.2026; örnekler: NYSE/ETF ve TEFAS dökümleri).
 *
 * Biçim (`;`, Türkçe sayı, çoğunlukla windows-1254):
 *   Hesap ; Fon | Fon adı ; Tarih ; "+ Alınan / - Satılan" ; Birim Fiyat (TL|$) ;
 *   Masraf (TL|$) ; Aciklama ; Portföy Değiştirme / Dengeleme [; TCMB Dolar Kuru]
 *
 * Şablondan farkları ve çevirisi:
 *  - Tür sütunu yok: miktarın işareti söyler (+ alış, − satış).
 *  - Para birimi sütunu yok: fiyat/masraf başlığındaki parantezden ($ → USD,
 *    TL → TRY). İki başlık farklı para söylüyorsa dosya tanınmaz.
 *  - `ETF:SMH` önekli; öneksiz TL fon kodu TEFAS fonudur (Piyasa = TEFAS).
 *  - Dengeleme "Evet" → nota yazılır. TCMB kuru yok sayılır (kur merkezden).
 *  - Geri yatırılan temettü: alış + eksi masraf + "temettü" — genel okuyucu tanır.
 *  - Birim fiyatı boş TEFAS satırı: fonun günde tek fiyatı var, tarih fiyat
 *    tarihi (kullanıcı, 09.10.2026) → fiyat o günün TEFAS arşivinden (ekran).
 *  - Dışa aktarıma PK eklenirse (`Kaynak kimliği`) kalıcı kimlik ondan türer.
 *
 * Satırlar şablon sütunlarına çevrilip GENEL okuyucudan (`dosyayiOku`) geçer:
 * kurallar tek kopya. Sonuçtaki başlık ve ham satırlar KULLANICININ dosyasının
 * kendisidir (sorunlu satırlar indirilince kendi satırlarını görür; kalıcı
 * kimlik de kendi satırından türer).
 */

import { isKnownCurrency } from "@parafon/contracts";
import type { Coverage, DailyBar, Decimal, IsoDate } from "@parafon/contracts";
import { M } from "../dil";
import { ayracBul, csvOku, csvYaz, dosyayiOku, sade, type Okuma, type OkunanSatir, type SayiBicimi, type Sorun } from "./iceAktarma";

const AD = M.iceAktar.oncesi.ad;

const SUTUN = {
  hesap: ["hesap"],
  fon: ["fon", "fon adi", "fon kodu"],
  tarih: ["tarih"],
  miktar: ["alinan satilan"],
  fiyat: ["birim fiyat"],
  masraf: ["masraf"],
  aciklama: ["aciklama"],
  dengeleme: ["portfoy degistirme dengeleme"],
  kimlik: ["kaynak kimligi", "kaynak id", "id", "pk"],
} as const;

type Anahtar = keyof typeof SUTUN;

/** Bilinen ama kullanılmayan sütunlar: "tanınmayan" denmez, neden yok sayıldığı söylenir. */
const YOK_SAYILAN = ["tcmb dolar kuru"];

/** Başlıktaki parantez: `Birim Fiyat ($)` → USD. */
export function basliktanPara(baslik: string): string | null | undefined {
  const m = /\(([^)]+)\)\s*$/.exec(baslik.trim());
  if (m === null) return null;
  const p = (m[1] ?? "").trim().toUpperCase();
  const kod = p === "$" ? "USD" : p === "TL" || p === "₺" ? "TRY" : p === "€" ? "EUR" : p;
  return isKnownCurrency(kod) ? kod : undefined;
}

export interface ParafonOncesiOkuma extends Okuma {
  readonly kaynakBicimi: string;
  readonly paraBirimi: string;
  readonly yokSayilan: readonly string[];
}

function evetMi(ham: string): boolean | null {
  const s = sade(ham);
  if (s === "evet" || s === "e") return true;
  if (s === "" || s === "hayir" || s === "h") return false;
  return null;
}

/** Dosya bu biçimdeyse okur; değilse `null` (genel okuyucuya bırakılır). */
export function parafonOncesiOku(metin: string, bicim: SayiBicimi): ParafonOncesiOkuma | null {
  const ayrac = ayracBul(metin);
  const tablo = csvOku(metin, ayrac);
  const baslik = tablo[0] ?? [];
  const yer: Partial<Record<Anahtar, number>> = {};
  const yokSayilan: string[] = [];
  const bilinmeyen: string[] = [];
  baslik.forEach((ham, i) => {
    // Sondaki para parantezi adın parçası değil: "Birim Fiyat (TL)" → "birim fiyat".
    const s = sade(ham.replace(/\([^)]*\)\s*$/, ""));
    const k = (Object.keys(SUTUN) as Anahtar[]).find((a) => (SUTUN[a] as readonly string[]).includes(s));
    if (k !== undefined && yer[k] === undefined) yer[k] = i;
    else if (YOK_SAYILAN.includes(s)) yokSayilan.push(ham.trim());
    else if (ham.trim() !== "") bilinmeyen.push(ham.trim());
  });
  // İmza: işaretli miktar + fon + hesap + tarih + fiyat.
  if (yer.miktar === undefined || yer.fon === undefined || yer.hesap === undefined || yer.tarih === undefined || yer.fiyat === undefined) {
    return null;
  }
  const paralar = [yer.fiyat, yer.masraf].filter((i): i is number => i !== undefined).map((i) => basliktanPara(baslik[i] ?? ""));
  if (paralar.some((p) => p === undefined)) return null;
  const tanimli = [...new Set(paralar.filter((p): p is string => p !== null))];
  if (tanimli.length !== 1) return null;
  const paraBirimi = tanimli[0] as string;

  const al = (s: readonly string[], k: Anahtar): string => {
    const i = yer[k];
    return i === undefined ? "" : (s[i] ?? "").trim();
  };
  const cevrilmis: string[][] = [["Tarih", "Hesap", "Tür", "Varlık", "Piyasa", "Miktar", "Fiyat", "Para birimi", "Masraf", "Not", "Kaynak kimliği"]];
  const ekSorun = new Map<number, string>();
  tablo.slice(1).forEach((s, i) => {
    const miktarHam = al(s, "miktar").replace(/^[−–]/, "-");
    const satis = miktarHam.startsWith("-");
    const fon = al(s, "fon");
    const piyasa = paraBirimi === "TRY" && !fon.includes(":") ? "TEFAS" : "";
    const notlar: string[] = [];
    if (al(s, "aciklama") !== "") notlar.push(al(s, "aciklama"));
    const d = evetMi(al(s, "dengeleme"));
    if (d === true) notlar.push(M.iceAktar.not.dengeleme);
    if (d === null) ekSorun.set(i + 2, M.iceAktar.oncesi.dengelemeAnlasilmadi(al(s, "dengeleme")));
    cevrilmis.push([
      al(s, "tarih"),
      al(s, "hesap"),
      satis ? "Satış" : "Alış",
      fon,
      piyasa,
      satis ? miktarHam.slice(1) : miktarHam.replace(/^\+/, ""),
      al(s, "fiyat"),
      paraBirimi,
      al(s, "masraf"),
      notlar.join(" · "),
      al(s, "kimlik"),
    ]);
  });

  const o = dosyayiOku(csvYaz(cevrilmis, ";"), bicim, { bosFiyatTefasArsivinden: true });
  // Ham satırlar ve başlık kullanıcının dosyasından (satır numaraları aynı).
  const ham = (satirNo: number): readonly string[] => tablo[satirNo - 1] ?? [];
  const ekli = (satirNo: number, mesajlar: readonly string[]): readonly string[] => {
    const ek = ekSorun.get(satirNo);
    return ek === undefined ? mesajlar : [...mesajlar, ek];
  };
  const sorunlar: Sorun[] = o.sorunlar.map((x) => ({ satirNo: x.satirNo, ham: ham(x.satirNo), mesajlar: ekli(x.satirNo, x.mesajlar) }));
  const satirlar: OkunanSatir[] = [];
  for (const x of o.satirlar) {
    if (ekSorun.has(x.satirNo)) sorunlar.push({ satirNo: x.satirNo, ham: ham(x.satirNo), mesajlar: ekli(x.satirNo, []) });
    else satirlar.push({ ...x, ham: ham(x.satirNo) });
  }
  sorunlar.sort((a, b) => a.satirNo - b.satirNo);
  return {
    ...o,
    baslik,
    ayrac,
    satirlar,
    sorunlar,
    bilinmeyenSutunlar: bilinmeyen,
    kaynakBicimi: AD,
    paraBirimi,
    yokSayilan,
  };
}

/** Ekranın tek giriş noktası: önce bu biçim, değilse şablon okuyucusu. */
export function dosyaOku(metin: string, bicim: SayiBicimi): Okuma & Partial<Pick<ParafonOncesiOkuma, "kaynakBicimi" | "paraBirimi" | "yokSayilan">> {
  return parafonOncesiOku(metin, bicim) ?? dosyayiOku(metin, bicim);
}

// ---------------------------------------------------------------------------
// TEFAS arşivinden fiyat
// ---------------------------------------------------------------------------

export type ArsivFiyati =
  | { readonly tur: "var"; readonly fiyat: Decimal }
  | { readonly tur: "yok"; readonly sebep: string }
  /** Merkez bu günü henüz indirmedi; sonra yeniden denenebilir. */
  | { readonly tur: "bekleniyor"; readonly sebep: string };

/**
 * Bir günün arşiv fiyatı. Yalnızca O GÜNÜN barı — önceki ya da sonraki gün
 * yerine konmaz. Bar yoksa sebebi kapsam söyler: kapsam içinde ve boşlukta
 * değilse o gün fiyat yayımlanmamıştır ("bulunamadı"); kapsam dışında ama ufkun
 * içindeyse merkez henüz indirmedi ("sorulamadı" — bekleniyor).
 */
export function arsivFiyatiBul(tarih: IsoDate, bars: readonly DailyBar[], kapsam: Coverage | null): ArsivFiyati {
  const bar = bars.find((b) => b.date === tarih);
  if (bar !== undefined) return { tur: "var", fiyat: bar.close };
  const gun = gunAdi(tarih);
  if (kapsam === null || kapsam.from === null || kapsam.to === null) {
    return { tur: "bekleniyor", sebep: M.iceAktar.oncesi.arsivYok };
  }
  if (kapsam.horizonFrom !== null && tarih < kapsam.horizonFrom) {
    return { tur: "yok", sebep: M.iceAktar.oncesi.ufukOncesi(gun, gunAdi(kapsam.horizonFrom)) };
  }
  if (tarih < kapsam.from || tarih > kapsam.to) {
    return { tur: "bekleniyor", sebep: M.iceAktar.oncesi.indirilmedi(gun, gunAdi(kapsam.from), gunAdi(kapsam.to)) };
  }
  if (kapsam.gaps.some((g) => g.from <= tarih && tarih <= g.to)) {
    return { tur: "yok", sebep: M.iceAktar.oncesi.bosluk(gun) };
  }
  return { tur: "yok", sebep: M.iceAktar.oncesi.yayimlanmamis(gun) };
}

function gunAdi(iso: IsoDate): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/** Arşivden alınan fiyatın hareket notundaki izi. */
export function arsivNotu(tarih: IsoDate): string {
  return M.iceAktar.not.arsivFiyati(gunAdi(tarih));
}
